import { Dispatch, SetStateAction } from "react";
import { SharedAccess } from "target_wasm";
import { axiosInstance } from "contexts/axiosApiInstance";
import {
  activeOrgForRequests,
  StaleSessionError,
} from "@/components/main/stores/orgContextStore";
import { createLogger } from "debug/DebugingLogger";
import type { FetchSharedResult } from "@/components/canvas/detailsSide/tableDetails/TableServices";

const log = createLogger("DatasetServices");

/// A named, shareable SET of related tables. Mirrors the server `DatasetDto`.
/// Never "collection" in code or copy — the dataset vocabulary is locked.
/// The dataset grants zero table access — sharing reveals the list, never the
/// member tables (each viewer sees only members they can independently
/// discover).
export interface Dataset {
  id: string;
  name: string;
  description: string | null;
  /// Categorical palette short-name (a `cat-*` key, e.g. "sky"), assigned at
  /// creation. Resolve with `resolveDatasetColor` to a theme-correct color for
  /// the dataset icon and graph-mode node dots/legend.
  color: string;
  /// RFC3339 timestamp.
  created_at: string;
  /// Whether the current user owns this dataset (vs. sees it via a share).
  /// Only owners can add/remove members, share, or delete.
  is_owner: boolean;
  /// Published to everyone by a catalog publisher — visible to all users
  /// and its member tables are importable by all. Drives the "Public" badge.
  is_public: boolean;
  /// Role-owned catalog dataset: managed by any `can_manage_public_catalog`
  /// holder (not an individual owner) and survives its creator's deletion.
  /// Drives the "Catalog" tag.
  is_catalog: boolean;
  /// Licence display label (e.g. "CC BY 4.0"). Resolved server-side from
  /// `license_id`; free text only on rows predating it.
  license: string | null;
  /// SPDX identifier, or "other". The picker's selected state keys off this.
  license_id: string | null;
  /// Set only when `license_id` is "other".
  license_name: string | null;
  license_url: string | null;
  /// Free-text attribution / credit line. Surfaced on the public website.
  attribution: string | null;
  /// Assigned labels from the manager-managed catalog. Surfaced + filtered on
  /// the public website.
  labels: DatasetLabel[];
  /// Freetext search keywords. Distinct from labels: labels are the curated
  /// taxonomy the website facets on, these become schema.org `keywords`.
  keywords: string[];
  /// Assigned creators, in display order — WHO made the data, as resolvable
  /// entities. Separate from `attribution`, which is the licence's credit text.
  creators: DatasetOrganization[];
}

/// A label from the manager-managed global catalog (name-only). Mirrors the
/// server `DatasetLabel`.
export interface DatasetLabel {
  id: string;
  name: string;
}

/// One member of a dataset. Mirrors the server `DatasetMemberDto`.
export interface DatasetMember {
  table_id: string;
  /// The live table name when `discoverable`, else the remembered name-at-add.
  /// `null` ⇒ the member is obfuscated: a shared viewer with no access to it.
  /// The server withholds the name (and all metadata); only `table_id` remains.
  name: string | null;
  /// RFC3339 timestamp.
  added_at: string;
  /// True when the caller can currently discover this table (live data).
  /// False ⇒ `name` is the remembered fallback.
  discoverable: boolean;
  /// The member's parent model — present ONLY when the caller can view that
  /// model (owner / collaborator / viewer), so the client can render a link.
  /// Null when the caller lacks model access (or the table has no single
  /// parent model).
  parent_model_id: string | null;
  parent_model_name: string | null;
  /// The table is in its model's trash. Still a member (deleting a table never
  /// edits a dataset) and still readable until it's purged.
  scheduled_for_deletion: boolean;
  /// The table was permanently deleted. Never conflate with `discoverable:
  /// false` — that one means "ask for access", this one means "it's gone".
  hard_deleted: boolean;
}

/// A related link on a dataset. Mirrors the server `DatasetLinkDto`.
export interface DatasetLink {
  id: string;
  url: string;
  label: string | null;
  /// RFC3339 timestamp.
  added_at: string;
}

/// A dataset plus its members and related links, filtered per the calling
/// viewer.
export interface DatasetDetail extends Dataset {
  members: DatasetMember[];
  links: DatasetLink[];
}

/// List datasets the current user owns or has been shared. Membership is not
/// resolved here — call `getDataset` to expand a dataset's members.
///
/// D19: sends the active org so the server narrows to that context — a dataset
/// created inside a non-personal org lives there and nowhere else. No context means
/// the union view, which is what the page shows before an org is picked.
///
/// `unscoped` opts OUT, for callers asking about a RESOURCE rather than about
/// the current context. A dataset may list tables from several orgs (D21), so
/// "which datasets contain this table" has an answer the browsing context must
/// not change. Deliberately explicit, mirroring `fetchModels`: silently
/// dropping the narrowing for everyone would undo the boundary.
export const listDatasets = async (options?: {
  unscoped?: boolean;
}): Promise<Dataset[]> => {
  let activeOrgId: string | undefined;
  if (!options?.unscoped) {
    const org = activeOrgForRequests();
    if (org.kind === "stale") return [];
    activeOrgId = org.kind === "org" ? org.orgId : undefined;
  }
  const { data } = await axiosInstance.get<Dataset[]>("/action/datasets", {
    params: { org_id: activeOrgId },
  });
  return data;
};

/// Fetch a dataset with its members, filtered for the caller (owner sees all
/// rows; a shared viewer sees only members they can discover).
export const getDataset = async (id: string): Promise<DatasetDetail> => {
  const { data } = await axiosInstance.get<DatasetDetail>(
    `/action/datasets/${id}`,
  );
  return data;
};

/// Create an empty dataset owned by the caller, or — with `catalog` — a
/// role-owned catalog dataset (server-gated on `can_manage_public_catalog`).
export const createDataset = async (
  name: string,
  description?: string,
  catalog = false,
): Promise<Dataset> => {
  // D19: the dataset belongs to the org it is created IN, and that org decides
  // who it may be shared with. A catalog dataset is platform-scoped and carries
  // no org, so it must not claim a context. The server verifies membership
  // before stamping, so this is a hint the caller cannot forge.
  let activeOrgId: string | undefined;
  if (!catalog) {
    const org = activeOrgForRequests();
    if (org.kind === "stale") throw new StaleSessionError();
    activeOrgId = org.kind === "org" ? org.orgId : undefined;
  }
  const { data } = await axiosInstance.post<Dataset>("/action/datasets", {
    name,
    description: description?.trim() ? description.trim() : null,
    is_catalog: catalog,
    org_id: activeOrgId,
  });
  return data;
};

export const updateDataset = async (
  id: string,
  patch: {
    name?: string;
    description?: string | null;
    license_id?: string | null;
    license_name?: string | null;
    license_url?: string | null;
    attribution?: string | null;
    label_ids?: string[];
    creator_org_ids?: string[];
    keywords?: string[];
  },
): Promise<Dataset> => {
  const body: Record<string, unknown> = {};
  // `name` is required-non-empty (a dataset must keep a name); only send a
  // trimmed non-empty value — the server rejects blanks with 400 invalid_name.
  if ("name" in patch && patch.name?.trim()) body.name = patch.name.trim();
  if ("description" in patch)
    body.description = patch.description?.trim() || "";
  // An empty `license_id` clears the licence; the server resolves the label and
  // URL from the id, so neither is sent.
  if ("license_id" in patch) body.license_id = patch.license_id ?? "";
  if ("license_name" in patch) body.license_name = patch.license_name ?? "";
  if ("license_url" in patch) body.license_url = patch.license_url ?? "";
  if ("attribution" in patch)
    body.attribution = patch.attribution?.trim() || "";
  if ("label_ids" in patch) body.label_ids = patch.label_ids ?? [];
  // An empty array is meaningful (clear them); absent leaves them untouched.
  if ("creator_org_ids" in patch)
    body.creator_org_ids = patch.creator_org_ids ?? [];
  if ("keywords" in patch) body.keywords = patch.keywords ?? [];
  const { data } = await axiosInstance.patch<Dataset>(
    `/action/datasets/${id}`,
    body,
  );
  return data;
};

/// A creator-registry entry: WHO authored a dataset, as a resolvable entity.
/// Separate from `attribution`, which is the credit TEXT a licence requires a
/// reuser to display. Mirrors the server `OrganizationDto`.
export interface DatasetOrganization {
  id: string;
  /// "Organization" | "Person" — the only two schema.org accepts here.
  entity_type: string;
  /// Entity name only, never a sentence.
  name: string;
  /// The entity's own homepage, not the dataset's page.
  url?: string;
  /// Wikidata/Wikipedia URL — what lets a consumer resolve the entity.
  same_as?: string;
  /// Allowlisted schema.org long-tail properties. Extending this needs no
  /// migration: add the key to `ORG_EXTRA_KEYS` server-side and a descriptor
  /// in `ORG_FIELDS` client-side.
  extra: Record<string, string>;
}

export type OrganizationInput = Omit<DatasetOrganization, "id">;

export const listOrganizations = async (): Promise<DatasetOrganization[]> => {
  const { data } = await axiosInstance.get<DatasetOrganization[]>(
    "/action/datasets/organizations",
  );
  return data;
};

export const createOrganization = async (
  input: OrganizationInput,
): Promise<DatasetOrganization> => {
  const { data } = await axiosInstance.post<DatasetOrganization>(
    "/action/datasets/organizations",
    input,
  );
  return data;
};

export const updateOrganization = async (
  id: string,
  input: OrganizationInput,
): Promise<DatasetOrganization> => {
  const { data } = await axiosInstance.patch<DatasetOrganization>(
    `/action/datasets/organizations/${id}`,
    input,
  );
  return data;
};

export const deleteOrganization = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/action/datasets/organizations/${id}`);
};

/// Provenance + size of a dataset's maintenance guide — the panel row's
/// status line. Served without decrypting the guide body.
export interface MaintenanceGuideMeta {
  bytes: number;
  /// Unix millis.
  updated_at: number;
  /// Display name of the last author; absent when that account is gone.
  updated_by_display?: string;
}

export interface MaintenanceGuide {
  guide: string;
  /// Unix millis.
  updated_at: number;
  updated_by_display?: string;
}

/// 404 covers both "no guide" and "not yours to read" — the server keeps them
/// deliberately indistinguishable, so `null` here just means "show the empty
/// state".
const nullOn404 = (e: unknown): null => {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 404) return null;
  throw e;
};

export const getMaintenanceGuideMeta = async (
  id: string,
): Promise<MaintenanceGuideMeta | null> =>
  axiosInstance
    .get<MaintenanceGuideMeta>(`/action/datasets/${id}/maintenance-guide/meta`)
    .then((r) => r.data)
    .catch(nullOn404);

export const getMaintenanceGuide = async (
  id: string,
): Promise<MaintenanceGuide | null> =>
  axiosInstance
    .get<MaintenanceGuide>(`/action/datasets/${id}/maintenance-guide`)
    .then((r) => r.data)
    .catch(nullOn404);

/// `null` (or blank) deletes the guide.
export const putMaintenanceGuide = async (
  id: string,
  guide: string | null,
): Promise<void> => {
  await axiosInstance.put(`/action/datasets/${id}/maintenance-guide`, {
    guide,
  });
};

export const listLabels = async (): Promise<DatasetLabel[]> => {
  const { data } = await axiosInstance.get<DatasetLabel[]>(
    "/action/datasets/labels",
  );
  return data;
};

export const createLabel = async (name: string): Promise<DatasetLabel> => {
  const { data } = await axiosInstance.post<DatasetLabel>(
    "/action/datasets/labels",
    { name: name.trim() },
  );
  return data;
};

export const deleteLabel = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/action/datasets/labels/${id}`);
};

export const deleteDataset = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/action/datasets/${id}`);
};

export const datasetsContainingTable = async (
  tableId: string,
): Promise<string[]> => {
  const { data } = await axiosInstance.get<string[]>(
    `/action/datasets/containing/${tableId}`,
  );
  return data;
};

export interface TableDatasetMembership {
  id: string;
  name: string;
  color: string;
  is_owner: boolean;
  owner_name: string | null;
}

/// The datasets a table is in, as the caller may learn them: those they can
/// view are named (public ones always are), other people's are only counted.
export interface DatasetUsage {
  named: { id: string; name: string; is_public: boolean }[];
  private_count: number;
}

export const datasetUsageTotal = (usage: DatasetUsage | undefined): number =>
  (usage?.named.length ?? 0) + (usage?.private_count ?? 0);

/// Keyed by table id; a table the caller cannot edit is left out.
export const datasetUsageForTables = async (
  tableIds: string[],
): Promise<Record<string, DatasetUsage>> => {
  const { data } = await axiosInstance.post<Record<string, DatasetUsage>>(
    "/action/datasets/usage",
    { table_ids: tableIds },
  );
  return data;
};

export const datasetsForTable = async (
  tableId: string,
): Promise<TableDatasetMembership[]> => {
  const { data } = await axiosInstance.get<TableDatasetMembership[]>(
    `/action/datasets/for-table/${tableId}`,
  );
  return data;
};

/// Add a table to a dataset. `inserted` is false for the idempotent no-op (it
/// was already a member) — either way the table IS in the dataset on return.
/// A 2xx alone does not prove the caller will SEE it, so callers that confirm an
/// add to the user should re-read membership (see `datasetsContainingTable`).
export const addTableToDataset = async (
  id: string,
  tableId: string,
  name?: string,
): Promise<{ inserted: boolean }> => {
  const { data } = await axiosInstance.post<{ inserted?: boolean }>(
    `/action/datasets/${id}/tables`,
    {
      table_id: tableId,
      // The current display name, so the member's remembered name is correct
      // even for a just-created default-named table not yet in the search
      // projection.
      name: name ?? null,
    },
  );
  return { inserted: data?.inserted ?? true };
};

/// Remove a table from a dataset (owner only, idempotent).
export const removeTableFromDataset = async (
  id: string,
  tableId: string,
): Promise<void> => {
  await axiosInstance.delete(`/action/datasets/${id}/tables/${tableId}`);
};

export const addLinkToDataset = async (
  id: string,
  link: { url: string; label?: string },
): Promise<DatasetLink> => {
  const label = link.label?.trim();
  const { data } = await axiosInstance.post<DatasetLink>(
    `/action/datasets/${id}/links`,
    { url: link.url.trim(), label: label ? label : null },
  );
  return data;
};

export const removeLinkFromDataset = async (
  id: string,
  linkId: string,
): Promise<void> => {
  await axiosInstance.delete(`/action/datasets/${id}/links/${linkId}`);
};

export const shareDataset = async (
  id: string,
  input: {
    emails?: string[];
    shareOwnedTables: boolean;
  },
): Promise<{ invalidEmails: string[]; atCapacityEmails: string[] }> => {
  const { data } = await axiosInstance.post<{
    invalid_emails: string[];
    at_capacity_emails?: string[];
  }>(`/action/datasets/${id}/share`, {
    users: input.emails ?? [],
    share_owned_tables: input.shareOwnedTables,
  });
  return {
    invalidEmails: data.invalid_emails ?? [],
    atCapacityEmails: data.at_capacity_emails ?? [],
  };
};

export const revokeDatasetShare = async (
  id: string,
  target: { userEmail?: string },
): Promise<void> => {
  await axiosInstance.post(`/action/datasets/${id}/revoke`, {
    user_email: target.userEmail,
  });
};

export const fetchDatasetSharedWithList = async (
  datasetId: string,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  search?: string,
  setContinuationToken?: Dispatch<SetStateAction<string | null>>,
  continuationToken?: string | null,
  limit?: number,
): Promise<FetchSharedResult | null> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (continuationToken)
      params.append("continuation_token", continuationToken);
    if (limit !== undefined) params.append("limit", String(limit));
    const queryString = params.toString();
    const url = `/action/datasets/${datasetId}/shared_with/users${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    const data = response.data;

    const rawSharedAccess = {
      users: data.users ?? [],
    };
    const newAccess = SharedAccess.from_js(rawSharedAccess);
    const nextToken: string | null = data.continuation_token ?? null;

    if (continuationToken) {
      setSharedWithList((prev) =>
        prev ? prev.extend_js(rawSharedAccess) : newAccess,
      );
    } else {
      setSharedWithList((prev) =>
        prev ? prev.replace_users_js(rawSharedAccess) : newAccess,
      );
    }
    setContinuationToken?.(nextToken);
    return { sharedAccess: newAccess, continuationToken: nextToken };
  } catch (error) {
    log.error("Error fetching dataset shared-with list: %o", error);
    return null;
  }
};

export interface DatasetReleaseMember {
  table_id: string;
  checkpoint_id: string;
  version: number;
  kind: "releasable" | "external";
  release_name: string | null;
  name_at_release: string | null;
}

export interface DatasetRelease {
  id: string;
  version: number;
  label: string;
  released_by: string;
  released_at: string;
  members: DatasetReleaseMember[];
}

export interface DatasetReleases {
  releases: DatasetRelease[];
  canRelease: boolean;
  currentVersions: Record<string, number>;
  /// Table ids of members that block a release (connector-driven tables that
  /// can't be snapshotted and carry no released checkpoint). Manager-only; lets
  /// the UI explain why a release can't be cut instead of hiding the affordance.
  unreleasable: string[];
}

export async function listDatasetReleases(
  id: string,
): Promise<DatasetReleases> {
  const { data } = await axiosInstance.get<{
    releases: DatasetRelease[];
    can_release: boolean;
    current_versions: Record<string, number>;
    unreleasable?: string[];
  }>(`/action/datasets/${id}/releases`);
  return {
    releases: data.releases,
    canRelease: data.can_release,
    currentVersions: data.current_versions ?? {},
    unreleasable: data.unreleasable ?? [],
  };
}

/// A dataset member referenced in a validation failure.
export interface MemberRef {
  table_id: string;
  name: string;
}

/// A member whose declared primary key is invalid, with the offending counts.
export interface InvalidPrimaryKey extends MemberRef {
  null_key_rows: number;
  duplicate_groups: number;
}

/// The per-gate failures returned when a catalog release is hard-blocked.
export interface ReleaseValidationGates {
  /// True ⇒ the dataset itself is missing a description.
  dataset_description: boolean;
  /// True ⇒ no licence is declared. Either spelling satisfies the server: the
  /// SPDX `license_id` every new caller writes, or the legacy free-text
  /// `license` kept for rows that predate it.
  dataset_license: boolean;
  /// True ⇒ nobody is credited. A catalog release is public data, and a
  /// consumer needs to know who to credit before they can use it.
  dataset_attribution: boolean;
  missing_tables: MemberRef[];
  missing_descriptions: MemberRef[];
  invalid_primary_keys: InvalidPrimaryKey[];
  /// Members from outside the catalog — releasing would publish them.
  outside_catalog?: MemberRef[];
}

/// Thrown by `releaseDataset` when the server rejects a catalog release with the
/// quality-gate 409. Carries the exact failures so the UI can list them.
export class ReleaseValidationError extends Error {
  gates: ReleaseValidationGates;
  constructor(gates: ReleaseValidationGates) {
    super("release_validation_failed");
    this.name = "ReleaseValidationError";
    this.gates = gates;
  }
}

/// Cut a new release. `label` names it and becomes the checkpoint alias on each
/// releasable member. For a catalog dataset this also publishes it. Rejects
/// (409) when nothing changed, the label is reused, an imported member's source
/// has no release, or (catalog) a quality gate fails — the last is rethrown as a
/// `ReleaseValidationError` carrying the per-gate failures.
export async function releaseDataset(
  id: string,
  label: string,
): Promise<DatasetRelease> {
  try {
    const { data } = await axiosInstance.post<DatasetRelease>(
      `/action/datasets/${id}/release`,
      { label },
    );
    return data;
  } catch (e) {
    const body = (
      e as {
        response?: {
          data?: { error?: string; gates?: ReleaseValidationGates };
        };
      }
    )?.response?.data;
    if (body?.error === "release_validation_failed" && body.gates) {
      throw new ReleaseValidationError(body.gates);
    }
    throw e;
  }
}

/// Outcome of a referral ask, mirroring the values-access statuses.
export type DatasetTableAccessStatus =
  "sent" | "already_requested" | "already_readable" | "error";

/// Ask for access to a table you can only see because THIS dataset lists it.
///
/// The dataset id is the standing, not decoration: the caller cannot discover
/// the table, so the server verifies the referral (you can view this dataset,
/// and this dataset lists that table) instead of asking FGA about the table.
export const requestDatasetTableAccess = async (
  datasetId: string,
  tableId: string,
): Promise<DatasetTableAccessStatus> => {
  try {
    const { data } = await axiosInstance.post(
      `/action/datasets/${datasetId}/tables/${tableId}/request-access`,
      {},
    );
    const status = (data as { status?: string })?.status;
    if (
      status === "sent" ||
      status === "already_requested" ||
      status === "already_readable"
    ) {
      return status;
    }
    return "error";
  } catch (error) {
    log.error("Error requesting dataset table access:", error);
    return "error";
  }
};
